import { useMemo, useState } from "react";

import {
  POOL_PHASE_LABELS,
  type ActiveRollout,
  type PoolPhase,
  type RolloutRecord,
  useAbortRollout,
  useRollouts,
} from "@/api/local/rollout";
import { diffWorkloadSpec } from "@/api/local/workload";
import type { Service } from "@/api/types";
import { useConfirm } from "@/components/ConfirmDialog";
import { Modal } from "@/components/Modal";
import { Pill, type PillTone } from "@/components/Pill";
import { Section } from "@/components/Section";
import { WorkloadDiffView } from "@/components/WorkloadDiff";
import { useServices } from "@/workloads/data";

import {
  CheckCircle2,
  Filter,
  History,
  Loader2,
  Rocket,
  XCircle,
} from "lucide-react";

/**
 * Rollout history & live timeline.
 *
 * For now this is fed entirely from the local rollout store
 * (`webui/src/api/local/rollout.ts`); when the controller exposes a
 * real `/rollouts` endpoint we'll layer it on top with the same
 * shape.  Each row exposes a "view" button that opens a modal with
 * the spec diff (computed from the captured snapshots) and the
 * per-pool plan.
 */
export function GlobalRollouts() {
  const rollouts = useRollouts();
  const services = useServices();
  const confirm = useConfirm();
  const abortRollout = useAbortRollout();
  const [serviceFilter, setServiceFilter] = useState<number | "all">("all");
  const [scopeFilter, setScopeFilter] = useState<"all" | "service" | "pool">(
    "all",
  );
  const [stateFilter, setStateFilter] = useState<
    "all" | "running" | "completed" | "aborted"
  >("all");
  const [openRecord, setOpenRecord] = useState<RolloutRecord | null>(null);

  const serviceById = useMemo(() => {
    const map = new Map<number, Service>();
    for (const s of services.data ?? []) map.set(s.id, s);
    return map;
  }, [services.data]);

  const records = useMemo(() => {
    return rollouts.history.filter((r) => {
      if (serviceFilter !== "all" && r.service_id !== serviceFilter) return false;
      if (scopeFilter !== "all" && r.scope !== scopeFilter) return false;
      if (stateFilter !== "all") {
        // The running filter matches both "pending" (controller acked
        // but hasn't started rolling) and "running"; everything else is
        // a strict equality check.
        if (stateFilter === "running") {
          if (r.state !== "running" && r.state !== "pending") return false;
        } else if (r.state !== stateFilter) {
          return false;
        }
      }
      return true;
    });
  }, [rollouts.history, serviceFilter, scopeFilter, stateFilter]);

  const showActive =
    rollouts.active &&
    (serviceFilter === "all" || rollouts.active.service_id === serviceFilter) &&
    (scopeFilter === "all" || rollouts.active.scope === scopeFilter) &&
    (stateFilter === "all" || stateFilter === "running");

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <Filter size={14} className="muted" />
        <select
          value={String(serviceFilter)}
          onChange={(e) =>
            setServiceFilter(
              e.target.value === "all" ? "all" : Number(e.target.value),
            )
          }
        >
          <option value="all">all services</option>
          {(services.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={scopeFilter}
          onChange={(e) =>
            setScopeFilter(e.target.value as typeof scopeFilter)
          }
        >
          <option value="all">all scopes</option>
          <option value="service">service-scope</option>
          <option value="pool">pool-scope</option>
        </select>
        <select
          value={stateFilter}
          onChange={(e) =>
            setStateFilter(e.target.value as typeof stateFilter)
          }
        >
          <option value="all">any state</option>
          <option value="running">running</option>
          <option value="completed">completed</option>
          <option value="aborted">aborted</option>
        </select>
        <span className="muted" style={{ marginLeft: "auto", fontSize: 11.5 }}>
          {records.length} record{records.length === 1 ? "" : "s"}
          {showActive ? " · 1 in flight" : ""}
        </span>
      </div>

      {showActive && rollouts.active && (
        <Section
          title="In flight"
          icon={<Rocket />}
          count={<Pill tone="info" pulse>{rollouts.active.state}</Pill>}
          flush
        >
          <ActiveRolloutRow
            active={rollouts.active}
            service={serviceById.get(rollouts.active.service_id) ?? null}
          />
        </Section>
      )}

      <Section
        title="History"
        icon={<History />}
        count={`${records.length}`}
        flush
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 120 }}>State</th>
                <th>Scope</th>
                <th>Service</th>
                <th>Pool</th>
                <th style={{ textAlign: "right" }}>Pools</th>
                <th>Started</th>
                <th>Finished</th>
                <th style={{ textAlign: "right" }}>Strategy</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => {
                const svc = serviceById.get(r.service_id);
                const poolName =
                  r.scope === "pool"
                    ? (r.pool_progress[0]?.pool_name ??
                      `pool#${r.pool_id ?? "?"}`)
                    : "—";
                return (
                  <tr key={r.id} className="hoverable">
                    <td>
                      <Pill tone={stateTone(r.state)}>
                        {stateIcon(r.state)} {r.state}
                      </Pill>
                    </td>
                    <td>
                      <Pill tone={r.scope === "service" ? "brand" : "info"}>
                        {r.scope}
                      </Pill>
                    </td>
                    <td className="mono">{svc?.name ?? `#${r.service_id}`}</td>
                    <td className="mono muted">{poolName}</td>
                    <td className="num mono">{r.pool_progress.length}</td>
                    <td className="mono muted">
                      {new Date(r.started_at).toLocaleString()}
                    </td>
                    <td className="mono muted">
                      {r.finished_at
                        ? new Date(r.finished_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="num mono muted">
                      surge {r.strategy.surge_pct}% · drain{" "}
                      {r.strategy.drain_seconds}s
                    </td>
                    <td className="actions">
                      {(r.state === "running" || r.state === "pending") && (
                        <button
                          className="btn btn--danger btn--sm"
                          onClick={async () => {
                            const svcLabel = svc?.name ?? `#${r.service_id}`;
                            const ok = await confirm({
                              title: "Abort rollout?",
                              tone: "danger",
                              confirmLabel: "Abort rollout",
                              description: (
                                <>
                                  <p style={{ marginTop: 0 }}>
                                    Abort rollout{" "}
                                    <code className="mono">{r.id}</code> on{" "}
                                    <code className="mono">{svcLabel}</code>?
                                  </p>
                                  <p
                                    className="muted"
                                    style={{ fontSize: 12, marginBottom: 0 }}
                                  >
                                    Green replicas already provisioned will be
                                    left alive so you can investigate.
                                  </p>
                                </>
                              ),
                            });
                            if (!ok) return;
                            void abortRollout(r.id);
                          }}
                        >
                          <XCircle size={11} /> abort
                        </button>
                      )}
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => setOpenRecord(r)}
                      >
                        view
                      </button>
                    </td>
                  </tr>
                );
              })}
              {records.length === 0 && !showActive && (
                <tr>
                  <td colSpan={9} className="empty">
                    no rollouts match the filter — they&rsquo;ll show up here
                    automatically once you start rolling things out
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <p
        className="muted"
        style={{ fontSize: 11.5, marginTop: 14, lineHeight: 1.5 }}
      >
        Sourced from the in-browser rollout store while the controller
        ships its <code>/rollouts</code> endpoint. Records survive
        reload but live only on this device. Rollout-related events
        (image pulls, replica health flips, traffic switches) will also
        land in the controller event stream — see{" "}
        <strong>Events</strong>.
      </p>

      <RolloutDetailModal
        record={openRecord}
        service={openRecord ? serviceById.get(openRecord.service_id) : null}
        onClose={() => setOpenRecord(null)}
      />
    </>
  );
}

function ActiveRolloutRow({
  active,
  service,
}: {
  active: ActiveRollout;
  service: Service | null;
}) {
  const completed = active.plan.filter((p) => p.phase === "complete").length;
  const total = active.plan.length;
  return (
    <div
      className="rollout-pool"
      style={{ marginBottom: 0, borderRadius: "var(--radius-md)" }}
    >
      <div className="rollout-pool__head">
        <div className="rollout-pool__name mono">
          {service?.name ?? `#${active.service_id}`}
        </div>
        <div className="rollout-pool__phase">
          <Pill tone={active.scope === "service" ? "brand" : "info"}>
            {active.scope}
          </Pill>
          <Pill tone="brand">blue/green</Pill>
        </div>
        <div className="rollout-pool__pct mono">
          {completed}/{total}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {active.plan.map((p) => (
          <div
            key={p.pool_id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
            }}
          >
            <span className="mono" style={{ flex: "1 1 auto" }}>
              {p.pool_name}
            </span>
            <Pill tone={phaseTone(p.phase)} pulse={phaseTone(p.phase) === "info"}>
              {POOL_PHASE_LABELS[p.phase]}
            </Pill>
          </div>
        ))}
      </div>
    </div>
  );
}

function RolloutDetailModal({
  record,
  service,
  onClose,
}: {
  record: RolloutRecord | null;
  service: Service | null | undefined;
  onClose: () => void;
}) {
  const diffs = useMemo(() => {
    if (!record || !record.target_workload) return [];
    return diffWorkloadSpec(record.source_workload, record.target_workload);
  }, [record]);

  if (!record) return null;
  const durationSec = record.finished_at
    ? Math.max(
        0,
        Math.round(
          (new Date(record.finished_at).getTime() -
            new Date(record.started_at).getTime()) /
            1000,
        ),
      )
    : Math.max(
        0,
        Math.round(
          (Date.now() - new Date(record.started_at).getTime()) / 1000,
        ),
      );

  return (
    <Modal
      open={!!record}
      onClose={onClose}
      size="xl"
      title={`Rollout · ${record.id}`}
      subtitle={
        <span>
          {service?.name ?? `service #${record.service_id}`} ·{" "}
          {record.scope === "pool"
            ? `pool ${record.pool_progress[0]?.pool_name ?? `#${record.pool_id}`}`
            : `${record.pool_progress.length} pool${record.pool_progress.length === 1 ? "" : "s"}`}{" "}
          · {durationSec}s
        </span>
      }
      headerExtras={
        <>
          <Pill tone={record.scope === "service" ? "brand" : "info"}>
            {record.scope}
          </Pill>
          <Pill tone="brand">blue/green</Pill>
          <Pill tone={stateTone(record.state)}>{record.state}</Pill>
        </>
      }
      footer={
        <>
          <div className="right" />
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="modal-section">
        <div className="modal-section__title">Spec changes</div>
        {record.target_workload ? (
          <WorkloadDiffView diffs={diffs} />
        ) : (
          <div className="diff-empty">
            No snapshots captured for this rollout (older record).
          </div>
        )}
      </div>

      <div className="modal-section">
        <div className="modal-section__title">Pools rolled</div>
        <table className="data plan-table">
          <thead>
            <tr>
              <th>Pool</th>
              <th>Final phase</th>
            </tr>
          </thead>
          <tbody>
            {record.pool_progress.map((p) => (
              <tr key={p.pool_id}>
                <td className="mono">{p.pool_name}</td>
                <td>
                  <Pill tone={phaseTone(p.phase)}>
                    {POOL_PHASE_LABELS[p.phase]}
                  </Pill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="modal-section">
        <div className="modal-section__title">Strategy</div>
        <div
          className="kv"
          style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}
        >
          <span className="k">surge</span>
          <span className="v">{record.strategy.surge_pct}%</span>
          <span className="k">drain</span>
          <span className="v">{record.strategy.drain_seconds}s</span>
          <span className="k">health grace</span>
          <span className="v">
            {record.strategy.health_check_grace_seconds}s
          </span>
          <span className="k">parallel pools</span>
          <span className="v">{record.strategy.max_concurrent_pools}</span>
          <span className="k">target hash</span>
          <span className="v">{record.target_hash}</span>
          <span className="k">source hash</span>
          <span className="v">{record.source_hash ?? "(first deploy)"}</span>
        </div>
      </div>
    </Modal>
  );
}

function stateTone(s: RolloutRecord["state"] | "running"): PillTone {
  if (s === "completed") return "ok";
  if (s === "running") return "info";
  return "danger";
}

function stateIcon(s: RolloutRecord["state"]) {
  if (s === "completed") return <CheckCircle2 size={11} />;
  return <XCircle size={11} />;
}

function phaseTone(p: PoolPhase): PillTone {
  if (p === "complete") return "ok";
  if (p === "failed") return "danger";
  if (p === "queued") return "neutral";
  return "info";
}

// Preserve loader icon import path for future inline progress.
void Loader2;
